import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { ADMIN_API_PREFIX, API_PREFIX, BASE_URL } from '@CONSTANTS/API.constant';
import userRepository from '@REPOSITORIES/local-repository/user-repository';
import { Area, CreateAreaParams, UpdateAreaParams } from '@INTERFACES/api/areas';

// Read API (uses API_PREFIX for read operations)
const areasReadApi = createApi({
    reducerPath: 'areas-read-api',
    baseQuery: fetchBaseQuery({
        baseUrl: BASE_URL + API_PREFIX,
        prepareHeaders: (headers) => {
            const token = userRepository.getData();

            if (token) {
                headers.set('Authorization', `Bearer ${token}`);
            }

            return headers;
        },
    }),
    tagTypes: ['Area'],
    endpoints: (builder) => ({
        getAll: builder.query<Area[], void>({
            query: () => '/areas/',
            providesTags: ['Area'],
        }),
        getById: builder.query<Area, number>({
            query: (id) => `/areas/${id}/`,
            providesTags: (result, error, id) => [{ type: 'Area', id }],
        }),
    }),
});

// Write API (uses ADMIN_API_PREFIX for write operations)
const areasWriteApi = createApi({
    reducerPath: 'areas-write-api',
    baseQuery: fetchBaseQuery({
        baseUrl: BASE_URL + ADMIN_API_PREFIX,
        prepareHeaders: (headers) => {
            const token = userRepository.getData();

            if (token) {
                headers.set('Authorization', `Bearer ${token}`);
            }

            return headers;
        },
    }),
    tagTypes: ['Area'],
    endpoints: (builder) => ({
        create: builder.mutation<Area, CreateAreaParams>({
            query: (params) => ({
                url: '/areas/',
                method: 'POST',
                body: params,
                headers: {
                    'Content-Type': 'application/json',
                },
            }),
            invalidatesTags: ['Area'],
        }),
        update: builder.mutation<Area, UpdateAreaParams>({
            query: ({ id, ...params }) => ({
                url: `/areas/${id}/`,
                method: 'PUT',
                body: params,
                headers: {
                    'Content-Type': 'application/json',
                },
            }),
            invalidatesTags: (result, error, { id }) => [{ type: 'Area', id }],
        }),
        partialUpdate: builder.mutation<Area, UpdateAreaParams>({
            query: ({ id, ...params }) => ({
                url: `/areas/${id}/`,
                method: 'PATCH',
                body: params,
                headers: {
                    'Content-Type': 'application/json',
                },
            }),
            invalidatesTags: (result, error, { id }) => [{ type: 'Area', id }],
        }),
        delete: builder.mutation<void, number>({
            query: (id) => ({
                url: `/areas/${id}/`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Area'],
        }),
    }),
});

// Export both APIs for middleware and reducer registration
export const areasAdminApi = areasWriteApi; // Main API for backwards compatibility
export { areasReadApi };

// Export hooks from both APIs
export const {
    useGetAllQuery: useGetAllAreas,
    
} = areasReadApi;

export const {
    useCreateMutation: useCreateArea,
    useUpdateMutation: useUpdateArea,
    
    useDeleteMutation: useDeleteArea,
} = areasWriteApi;
